// TempSessionsAdminTab - Admin interface for managing temporary public sessions
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { tempSessionAPI, workspaceAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CalendarClock, Plus, Search, Loader2, Clock, Calendar, Link2, Copy,
  Eye, Trash2, CheckCircle, AlertCircle, Edit, ExternalLink, Users,
  BarChart3, QrCode, RefreshCw, CopyPlus
} from 'lucide-react';
import { toast } from 'sonner';

const TIMEZONES = [
  'Europe/Paris', 'Europe/London', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'Asia/Tokyo', 'Asia/Shanghai',
  'Africa/Tunis', 'Africa/Casablanca', 'UTC',
];

export default function TempSessionsAdminTab() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  // Duplicate dialog
  const [duplicateSession, setDuplicateSession] = useState(null);
  const [duplicateTargetWs, setDuplicateTargetWs] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    workspace_id: '',
    title: '',
    description: '',
    start_at: '',
    end_at: '',
    timezone: 'Europe/Paris',
    attendance_enabled: false,
  });

  const loadData = useCallback(async () => {
    try {
      const [sessRes, wsRes] = await Promise.all([
        tempSessionAPI.list(),  // No workspace_id = get all
        workspaceAPI.list().catch(() => ({ data: [] })),
      ]);
      setSessions(sessRes.data || []);
      setWorkspaces(wsRes.data || []);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    if (!form.start_at || !form.end_at) { toast.error('Les dates sont requises'); return; }
    if (!form.workspace_id) { toast.error('Le workspace est requis'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      };
      const res = await tempSessionAPI.create(form.workspace_id, payload, user?.id);
      toast.success('Session créée !');
      setCreateOpen(false);
      setForm({
        workspace_id: '', title: '', description: '',
        start_at: '', end_at: '', timezone: 'Europe/Paris', attendance_enabled: false,
      });
      loadData();
      // Navigate to session editor
      navigate(`/workspace/${form.workspace_id}/temp-sessions/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const session = sessions.find(s => s.id === deleteId);
    try {
      await tempSessionAPI.delete(session?.workspace_id, deleteId);
      toast.success('Session supprimée');
      setSessions(s => s.filter(x => x.id !== deleteId));
    } catch (err) {
      toast.error('Erreur de suppression');
    } finally {
      setDeleteId(null);
    }
  };

  const handlePublish = async (session) => {
    try {
      await tempSessionAPI.publish(session.workspace_id, session.id);
      toast.success('Session publiée !');
      loadData();
    } catch (err) {
      toast.error('Erreur de publication');
    }
  };

  const copyLink = (session) => {
    const SESSION_DOMAIN = 'https://session-linux.efi-academy.com';
    const slug = session.custom_slug || session.slug;
    let url;
    if (slug) {
      url = `${SESSION_DOMAIN}/session/${slug}`;
    } else if (session.public_token) {
      url = `${SESSION_DOMAIN}/session/${session.public_token}`;
    } else {
      toast.error('Aucun lien disponible');
      return;
    }
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const openDuplicateDialog = (session) => {
    setDuplicateSession(session);
    setDuplicateTargetWs(session.workspace_id);
  };

  const handleDuplicate = async () => {
    if (!duplicateSession) return;
    setDuplicating(true);
    try {
      const targetWs = duplicateTargetWs || duplicateSession.workspace_id;
      const res = await tempSessionAPI.duplicate(
        duplicateSession.workspace_id, duplicateSession.id, user?.id,
        targetWs !== duplicateSession.workspace_id ? targetWs : undefined
      );
      toast.success('Session dupliquée !');
      setDuplicateSession(null);
      loadData();
      navigate(`/workspace/${targetWs}/temp-sessions/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la duplication');
    } finally {
      setDuplicating(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-700 border-gray-200' },
      active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      expired: { label: 'Expirée', className: 'bg-red-50 text-red-700 border-red-200' },
    };
    const s = map[status] || map.draft;
    return <Badge variant="outline" className={s.className}>{s.label}</Badge>;
  };

  const getWorkspaceName = (wsId) => {
    const ws = workspaces.find(w => w.id === wsId);
    return ws?.name || wsId;
  };

  const filtered = sessions.filter(s => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-xl border border-violet-500/30">
            <CalendarClock className="h-6 w-6 text-violet-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Sessions Temporaires</h2>
            <p className="text-sm text-muted-foreground">Sessions publiques accessibles via lien unique</p>
          </div>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
        >
          <Plus className="h-4 w-4" /> Créer une session
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="p-2 bg-blue-500/20 rounded-lg"><Calendar className="h-5 w-5 text-blue-400" /></div>
            <div>
              <p className="text-2xl font-bold">{sessions.length}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/20">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="p-2 bg-emerald-500/20 rounded-lg"><CheckCircle className="h-5 w-5 text-emerald-400" /></div>
            <div>
              <p className="text-2xl font-bold">{sessions.filter(s => s.status === 'active').length}</p>
              <p className="text-xs text-muted-foreground">Actives</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/20">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="p-2 bg-amber-500/20 rounded-lg"><AlertCircle className="h-5 w-5 text-amber-400" /></div>
            <div>
              <p className="text-2xl font-bold">{sessions.filter(s => s.status === 'draft').length}</p>
              <p className="text-xs text-muted-foreground">Brouillons</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="p-2 bg-purple-500/20 rounded-lg"><Eye className="h-5 w-5 text-purple-400" /></div>
            <div>
              <p className="text-2xl font-bold">{sessions.reduce((a, s) => a + (s.visit_count || 0), 0)}</p>
              <p className="text-xs text-muted-foreground">Visites</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex gap-2">
          {['all', 'draft', 'active', 'expired'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-primary text-white' : ''}
            >
              {f === 'all' ? 'Toutes' : f === 'draft' ? 'Brouillons' : f === 'active' ? 'Actives' : 'Expirées'}
            </Button>
          ))}
        </div>
      </div>

      {/* Sessions Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CalendarClock className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-semibold mb-1">Aucune session</p>
          <p className="text-sm">{search ? 'Aucun résultat' : 'Créez votre première session temporaire'}</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Workspace</th>
                    <th>Statut</th>
                    <th>Période</th>
                    <th>Visites</th>
                    <th>Contenus</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(session => (
                    <tr key={session.id}>
                      <td>
                        <div>
                          <p
                            className="font-semibold cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate(`/workspace/${session.workspace_id}/temp-sessions/${session.id}`)}
                          >
                            {session.title}
                          </p>
                          {session.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{session.description}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge variant="outline" className="text-xs">
                          {getWorkspaceName(session.workspace_id)}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          {statusBadge(session.status)}
                          {session.password_protected && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[10px] px-1.5">🔒</Badge>
                          )}
                          {session.attendance_enabled && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 text-[10px] px-1.5">📋</Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(session.start_at)}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            → {formatDate(session.end_at)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                          {session.visit_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className="flex items-center gap-1">
                          <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                          {session.items?.length || 0}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          {session.status === 'draft' && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => handlePublish(session)}
                              className="h-7 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" /> Publier
                            </Button>
                          )}
                          {session.public_token && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => copyLink(session)}>
                              <Copy className="h-3 w-3 mr-1" /> Lien
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0"
                            onClick={() => navigate(`/workspace/${session.workspace_id}/temp-sessions/${session.id}`)}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline"
                            className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openDuplicateDialog(session)}
                            title="Dupliquer la session">
                            <CopyPlus className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline"
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteId(session.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Session Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-violet-400" />
              Créer une session temporaire
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Workspace *</Label>
              <Select value={form.workspace_id} onValueChange={v => setForm({ ...form, workspace_id: v })}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un workspace" /></SelectTrigger>
                <SelectContent>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.id} value={ws.id}>{ws.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                placeholder="ex: Formation Docker - Avril 2026"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Description optionnelle..."
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Début *</Label>
                <Input type="datetime-local" value={form.start_at}
                  onChange={e => setForm({ ...form, start_at: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Fin *</Label>
                <Input type="datetime-local" value={form.end_at}
                  onChange={e => setForm({ ...form, end_at: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fuseau horaire</Label>
              <Select value={form.timezone} onValueChange={v => setForm({ ...form, timezone: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div>
                <p className="text-sm font-medium">Feuille de présence</p>
                <p className="text-xs text-muted-foreground">Activer le pointage journalier</p>
              </div>
              <Switch
                checked={form.attendance_enabled}
                onCheckedChange={v => setForm({ ...form, attendance_enabled: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {saving ? 'Création...' : 'Créer la session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette session ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Tous les contenus, logs et présences seront supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Dialog with Workspace Picker */}
      <Dialog open={!!duplicateSession} onOpenChange={() => setDuplicateSession(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CopyPlus className="h-5 w-5 text-blue-400" />
              Dupliquer la session
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Dupliquer <strong>{duplicateSession?.title}</strong>
            </p>
            <div className="space-y-2">
              <Label>Workspace de destination</Label>
              <Select value={duplicateTargetWs} onValueChange={setDuplicateTargetWs}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un workspace" /></SelectTrigger>
                <SelectContent>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.id} value={ws.id}>
                      {ws.name}
                      {ws.id === duplicateSession?.workspace_id && ' (source)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateSession(null)}>Annuler</Button>
            <Button onClick={handleDuplicate} disabled={duplicating}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
              {duplicating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CopyPlus className="h-4 w-4 mr-2" />}
              {duplicating ? 'Duplication...' : 'Dupliquer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
